/**
 * Folder Service - API Implementation
 * Servicio de carpetas usando la API del servidor
 * @module services/folderServiceApi
 */

import { apiClient } from './apiClient'

// Types
export interface Folder {
  id: string
  name: string
  description?: string | null
  parentId?: string | null
  level: number
  createdAt: string
  updatedAt: string
  parent?: { id: string; name: string } | null
  children?: Folder[]
  nodes?: { id: string; tipo: string; nombre: string }[]
  _count?: { children: number; nodes: number }
}

export interface CreateFolderData {
  name: string
  description?: string
  parentId?: string | null
}

export interface UpdateFolderData {
  name?: string
  description?: string | null
  parentId?: string | null
}

export const folderServiceApi = {
  /**
   * Obtener todas las carpetas
   */
  async getAll(): Promise<Folder[]> {
    return apiClient.get<Folder[]>('/api/folders')
  },

  /**
   * Obtener árbol jerárquico de carpetas
   */
  async getTree(): Promise<Folder[]> {
    return apiClient.get<Folder[]>('/api/folders/tree')
  },

  /**
   * Obtener carpeta por ID
   */
  async getById(id: string): Promise<Folder | null> {
    try {
      return await apiClient.get<Folder>(`/api/folders/${id}`)
    } catch (error) {
      return null
    }
  },

  /**
   * Crear nueva carpeta
   */
  async create(data: CreateFolderData): Promise<Folder> {
    return apiClient.post<Folder>('/api/folders', data)
  },

  /**
   * Crear carpeta raíz
   */
  async createRootFolder(name: string, description?: string): Promise<Folder> {
    return this.create({ name, description, parentId: null })
  },

  /**
   * Crear subcarpeta
   */
  async createSubfolder(parentId: string, name: string, description?: string): Promise<Folder> {
    return this.create({ name, description, parentId })
  },

  /**
   * Actualizar carpeta
   */
  async update(id: string, data: UpdateFolderData): Promise<Folder> {
    return apiClient.put<Folder>(`/api/folders/${id}`, data)
  },

  /**
   * Eliminar carpeta
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/folders/${id}`)
  },

  /**
   * Obtener carpetas raíz
   */
  async getRootFolders(): Promise<Folder[]> {
    const folders = await this.getAll()
    return folders.filter(f => !f.parentId)
  },

  /**
   * Obtener carpetas hijas
   */
  async getChildren(parentId: string): Promise<Folder[]> {
    const folders = await this.getAll()
    return folders.filter(f => f.parentId === parentId)
  },

  /**
   * Agregar nodo a carpeta
   */
  async addNodeToFolder(folderId: string, nodeId: string): Promise<void> {
    await apiClient.post(`/api/folders/${folderId}/nodes/${nodeId}`, {})
  },

  /** Asigna varios nodos a la carpeta (cualquier folderId previo dentro del tenant) */
  async assignNodesBatch(folderId: string, nodeIds: string[]): Promise<{ updated: number }> {
    return apiClient.post<{ updated: number }>(`/api/folders/${folderId}/nodes/batch`, { nodeIds })
  },

  /**
   * Remover nodo de carpeta
   */
  async removeNodeFromFolder(folderId: string, nodeId: string): Promise<void> {
    await apiClient.delete(`/api/folders/${folderId}/nodes/${nodeId}`)
  },

  /**
   * Mover nodos entre carpetas
   */
  async moveNodes(sourceFolderId: string, targetFolderId: string, nodeIds: string[]): Promise<void> {
    await apiClient.post(`/api/folders/${sourceFolderId}/move/${targetFolderId}`, { nodeIds })
  },
}
